import {
  productFamilyIdFormat,
  productIdFormat,
  supplierIdFormat,
} from "@redotech/redo-model/ids";
import { AdvancedFilterDataSchema } from "@redotech/redo-model/views/views";
import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod/zod-enum";
import * as z from "zod/v4";

const forecastingProductSupplierSchema_ = z.object({
  id: supplierIdFormat.schema,
  name: z.string(),
});

export const ForecastingProductSupplierSchema: z.ZodType<ForecastingProductSupplier> =
  forecastingProductSupplierSchema_;
export interface ForecastingProductSupplier
  extends z.infer<typeof forecastingProductSupplierSchema_> {}

const missingReorderParamSchema = createEnum([
  "leadTime",
  "coverageDays",
  "forecast",
  "available",
]);
export type MissingReorderParam = z.infer<typeof missingReorderParamSchema>;

const forecastingProductSchema_ = z.object({
  id: productIdFormat.schema,
  productFamilyId: productFamilyIdFormat.schema,
  image: z.string().nullish(),
  title: z.string(),
  sku: z.string().nullish(),
  vendor: z.string().nullish(),
  stockoutDate: zExt.plainDate().nullish(),
  stockoutDateCapped: z.boolean().optional(),
  totalQuantity: z.number(),
  totalAvailable: z.number(),
  totalCommitted: z.number(),
  totalReserved: z.number(),
  moq: z.number().nullish(),
  leadTime: z.number().nullish(),
  buffer: z.number().nullish(),
  coverageDays: z.number().nullish(),
  supplier: ForecastingProductSupplierSchema.nullish(),
  // Reorder calculation fields
  reorderPoint: z.number().nullish(),
  daysUntilReorder: z.number().nullish(),
  unitsToOrder: z.number().nullish(),
  missingReorderParams: z.array(missingReorderParamSchema).optional(),
  // Purchase order info
  purchaseOrderCount: z.number().int(),
  // Inventory value
  inventoryValue: z.number().nullish(),
  inventoryValueCurrency: z.string().nullish(),
});

export const ForecastingProductSchema: z.ZodType<ForecastingProduct> =
  forecastingProductSchema_;
export interface ForecastingProduct
  extends z.infer<typeof forecastingProductSchema_> {}

export const getForecastingProducts = {
  name: "getForecastingProducts" as const,
  input: z.object({
    today: zExt.plainDate(),
    page: z.number().int().min(0).default(0),
    pageSize: z.number().int(),
    search: z.string().optional(),
    sortBy: createEnum([
      "title",
      "sku",
      "vendor",
      "daysOfStockRemaining",
      "totalAvailable",
      "totalQuantity",
      "totalCommitted",
      "totalReserved",
      "moq",
      "leadTime",
      "buffer",
      "coverageDays",
      "supplier",
      "reorderPoint",
      "daysUntilReorder",
      "unitsToOrder",
      "inventoryValue",
    ]).optional(),
    sortDirection: createEnum(["asc", "desc"]).optional(),
    advancedFiltersData: z.array(AdvancedFilterDataSchema).optional(),
  }),
  output: z.object({
    products: z.array(ForecastingProductSchema),
    totalCount: z.number().int(),
  }),
};
